import * as R from 'ramda'
import { batch } from 'react-redux'

import { fetchGraph2 } from 'src/lib/data/fetch-graph'
import { fetchGraphMany } from 'src/lib/data/fetch-graph-many'
import graph from 'src/lib/data/graph'
import {
  resolveAliasKey,
  mergeFragments,
  withoutStoredAttributes
} from 'src/lib/graph/walk'

import dbSlice from 'src/store/db/slice'
import { dbSelector } from 'src/store/db/selectors/base'

import * as bannerActions from 'src/store/action-methods/banner'

const { setEntity, setEntities, setCollection, setCollectionFetching } =
  dbSlice.actions

export const fetchEntity =
  ({ eid, query }) =>
  async dispatch => {
    const data = await dispatch(fetchGraph2(null, query, eid))
    dispatch(setEntity({ query, data }))
  }

export const fetchEntities = ops => async (dispatch, getState) => {
  const state = getState()
  const db = dbSelector(state)

  const opsMap = R.reduce(
    (acc, [eid, fragment]) =>
      R.assoc(eid, R.append(fragment, acc[eid] || []), acc),
    {},
    ops
  )

  for (const eid in opsMap) {
    const existing = R.propOr([], eid, opsMap)
    const merged = mergeFragments(existing)
    const stripped = withoutStoredAttributes(eid, merged, db)

    if (R.without([':db/id'], stripped).length) {
      opsMap[eid] = R.uniq([':db/id', ...stripped])
    } else {
      delete opsMap[eid]
    }
  }

  const mergedOps = R.toPairs(opsMap)

  // group eids by equal fragments [[eids, fragment] ...]
  const groupedPairs = R.pipe(
    R.groupWith(([, fragmentA], [, fragmentB]) =>
      R.equals(fragmentA, fragmentB)
    ),
    R.map(pairs => [R.map(R.head, pairs), R.last(R.head(pairs))])
  )(mergedOps)

  if (groupedPairs.length) {
    const graphRes = await dispatch(fetchGraphMany(null, groupedPairs))

    dispatch(setEntities({ query: groupedPairs, data: graphRes }))
  }
}

export const fetchCollection =
  ({ eid, resolver, fragment, graph2: isGraph2, params }) =>
  async (dispatch, getState) => {
    const state = getState()
    const db = dbSelector(state)
    const alias = resolveAliasKey(resolver)
    const collection = R.path([eid, alias], db)

    const collectionParams = {
      keys: fragment && R.uniq([':db/id', ...fragment]),
      ...params
    }

    // check if anything has actually changed
    const keysToCheck = [
      'eid',
      'eids',
      'query',
      'boardId',
      '@offset',
      '@limit',
      '@order',
      '@sortCollection',
      '@filterCollection',
      'limit',
      'sort',
      'offset',
      'withMeta'
    ]

    if (
      collection &&
      R.equals(
        R.pick(keysToCheck, collectionParams),
        R.pick(keysToCheck, collection)
      )
    ) {
      if (!collectionParams['@asof']) {
        return
      }
    }

    dispatch(setCollectionFetching({ eid, alias, fetching: true }))

    try {
      const query = [':db/id', { [resolver]: collectionParams }]
      const res = isGraph2
        ? await dispatch(fetchGraph2(null, query, eid))
        : await graph(null, query, eid)

      dispatch(setCollection({ eid, alias, query, res }))
    } catch (e) {
      batch(() => {
        dispatch(
          bannerActions.setBanner({
            open: true,
            type: 'error',
            msg: R.pathOr(
              'Something went wrong.',
              ['response', 'body', 'cause'],
              e
            ),
            assertive: true
          })
        )
        dispatch(setCollectionFetching({ eid, alias, fetching: false }))
      })
    }
  }
